/**
 * Puppy CAD 内核 `math_FunctionRoots` 新编译分支的 TypeScript 等价实现。
 *
 * 职责：在指定参数区间扫描符号变化根、采样点零根和偶重极值根，并使用 Brent 型
 * 插值/二分与导数 Newton 改善恢复根参数。历史固定阈值按 Puppy CAD 内核 源码保留。
 */
export class FunctionRootsSolver {
    /** Puppy CAD 内核 Brent 最大迭代次数。 */
    private static readonly MAXIMUM_ITERATIONS = 100;

    /** Puppy CAD 内核 Brent 相对机器阈值。 */
    private static readonly RELATIVE_EPSILON = 2e-14;

    /** Puppy CAD 内核 极值搜索黄金比例。 */
    private static readonly GOLDEN_RATIO = 0.61803399;

    /**
     * 搜索一元函数全部离散根。
     *
     * @param func 支持值和一阶导数的函数。
     * @param firstParameter 参数区间第一端。
     * @param lastParameter 参数区间第二端。
     * @param requestedSampleCount 调用方采样数。
     * @param epsilonX 参数收敛容差。
     * @param epsilonF 函数值容差。
     * @param targetValue 目标函数值，默认零。
     * @param epsilonNull 全区间严格空值判断容差。
     * @returns Puppy CAD 内核 等价求根结果。
     */
    public solve(
        func: ScalarFunction,
        firstParameter: number,
        lastParameter: number,
        requestedSampleCount: number,
        epsilonX: number,
        epsilonF: number,
        targetValue: number = 0,
        epsilonNull: number = 0,
    ): FunctionRootsResult {
        const start = Math.min(firstParameter, lastParameter);
        const end = Math.max(firstParameter, lastParameter);
        const intervalLength = end - start;
        if (!Number.isFinite(intervalLength) || intervalLength <= 0) {
            return {done: false, allNull: false, roots: []};
        }
        const subdivisionCount = Math.max(20, 2 * Math.max(1, Math.trunc(requestedSampleCount)));
        const step = intervalLength / subdivisionCount;
        const dedupeTolerance = 1e-10 * (Math.abs(start) + Math.abs(end));
        const resolvedEpsilonX = Math.max(epsilonX, dedupeTolerance);
        const parameters = Array.from({length: subdivisionCount + 1}, (_unused, index) => start + index * step);
        const values: number[] = [];
        for (const parameter of parameters) {
            const value = func.value(parameter);
            if (value === null || !Number.isFinite(value)) {
                return {done: false, allNull: false, roots: []};
            }
            values.push(value - targetValue);
        }
        const allNull = values.every((value) => Math.abs(value) <= epsilonNull);
        if (allNull) return {done: true, allNull: true, roots: []};

        const roots: number[] = [];
        for (let index = 0; index < subdivisionCount; index += 1) {
            const leftValue = values[index]!;
            const rightValue = values[index + 1]!;
            if ((leftValue < 0 && rightValue > 0) || (leftValue > 0 && rightValue < 0)) {
                const root = this.solveBracket(
                    func,
                    targetValue,
                    parameters[index]!,
                    parameters[index + 1]!,
                    resolvedEpsilonX,
                );
                if (root !== null) this.appendRoot(roots, root, dedupeTolerance);
            }
        }

        // Puppy CAD 内核 单独保留采样点精确零值和首尾 EpsF 根。
        values.forEach((value, index) => {
            const isEndpoint = index === 0 || index === values.length - 1;
            if (value === 0 || (isEndpoint && Math.abs(value) <= epsilonF)) {
                this.appendRoot(roots, parameters[index]!, dedupeTolerance);
            }
        });

        // 正局部极小或负局部极大可能对应偶重相切根，必须同时执行导数根和黄金极值补查。
        for (let index = 1; index < subdivisionCount; index += 1) {
            const previousValue = values[index - 1]!;
            const currentValue = values[index]!;
            const nextValue = values[index + 1]!;
            const isPositiveMinimum = currentValue > 0
                && currentValue <= previousValue
                && currentValue <= nextValue;
            const isNegativeMaximum = currentValue < 0
                && currentValue >= previousValue
                && currentValue >= nextValue;
            if (!isPositiveMinimum && !isNegativeMaximum) continue;

            const left = parameters[index - 1]!;
            const middle = parameters[index]!;
            const right = parameters[index + 1]!;
            const derivative = func.derivative(middle);
            if (derivative !== null && Math.abs(derivative) > Number.EPSILON) {
                const estimate = currentValue / derivative;
                if (Math.abs(estimate) >= 5 * step) continue;
            }
            const derivativeRoot = this.solveDerivativeBracket(func, left, right, epsilonX);
            const goldenRoot = this.solveGoldenExtremum(
                func,
                targetValue,
                left,
                right,
                isPositiveMinimum ? 1 : -1,
                dedupeTolerance,
            );
            const candidates = [derivativeRoot, goldenRoot].filter((value): value is number => value !== null);
            let bestRoot: number | null = null;
            let bestResidual = Number.POSITIVE_INFINITY;
            let bestDerivative = Number.POSITIVE_INFINITY;
            candidates.forEach((candidate) => {
                const value = func.value(candidate);
                const derivativeValue = func.derivative(candidate);
                if (value === null || Math.abs(value - targetValue) >= epsilonF) return;
                const residual = Math.abs(value - targetValue);
                const derivativeResidual = derivativeValue === null ? Number.POSITIVE_INFINITY : Math.abs(derivativeValue);
                if (
                    residual < bestResidual - epsilonF
                    || (Math.abs(residual - bestResidual) <= epsilonF && derivativeResidual < bestDerivative)
                ) {
                    bestRoot = candidate;
                    bestResidual = residual;
                    bestDerivative = derivativeResidual;
                }
            });
            if (bestRoot !== null) this.appendRoot(roots, bestRoot, dedupeTolerance);
        }

        return {done: true, allNull: false, roots};
    }

    /**
     * 使用 Brent 型插值/二分搜索符号变化根，并执行 Puppy CAD 内核 同类 Newton 改善。
     */
    private solveBracket(
        func: ScalarFunction,
        targetValue: number,
        first: number,
        last: number,
        tolerance: number,
    ): number | null {
        let left = first;
        let right = last;
        let leftValue = (func.value(left) ?? Number.NaN) - targetValue;
        let rightValue = (func.value(right) ?? Number.NaN) - targetValue;
        if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue) || leftValue * rightValue > 0) return null;
        let best = Math.abs(leftValue) <= Math.abs(rightValue) ? left : right;
        let converged = false;

        for (let iteration = 0; iteration < FunctionRootsSolver.MAXIMUM_ITERATIONS; iteration += 1) {
            const toleranceValue = FunctionRootsSolver.RELATIVE_EPSILON * Math.abs(best) + 0.5 * tolerance;
            if (Math.abs(right - left) * 0.5 < toleranceValue) {
                converged = true;
                break;
            }
            const denominator = rightValue - leftValue;
            let candidate = Math.abs(denominator) <= Number.EPSILON
                ? (left + right) * 0.5
                : right - rightValue * (right - left) / denominator;
            if (candidate <= Math.min(left, right) || candidate >= Math.max(left, right)) {
                candidate = (left + right) * 0.5;
            }
            const candidateValueRaw = func.value(candidate);
            if (candidateValueRaw === null) return null;
            const candidateValue = candidateValueRaw - targetValue;
            best = candidate;
            if (candidateValue === 0) {
                converged = true;
                break;
            }
            if (leftValue * candidateValue < 0) {
                right = candidate;
                rightValue = candidateValue;
            } else {
                left = candidate;
                leftValue = candidateValue;
            }
        }
        // Puppy CAD 内核 正常构建在 100 轮仍未收敛时静默丢弃该 bracket 根，但不会把整体 Done 置为 false。
        if (!converged) return null;

        // Puppy CAD 内核 收敛后最多执行约六次受 bracket 约束的 Newton 残差改善。
        for (let iteration = 0; iteration < 6; iteration += 1) {
            const value = func.value(best);
            const derivative = func.derivative(best);
            if (value === null || derivative === null || Math.abs(derivative) <= 1e-10) break;
            const candidate = best - (value - targetValue) / derivative;
            if (candidate < first || candidate > last) break;
            const candidateValue = func.value(candidate);
            if (candidateValue === null || Math.abs(candidateValue - targetValue) >= Math.abs(value - targetValue)) break;
            best = candidate;
        }
        return best;
    }

    /** 使用一阶导数符号变化二分搜索极值参数。 */
    private solveDerivativeBracket(
        func: ScalarFunction,
        first: number,
        last: number,
        tolerance: number,
    ): number | null {
        let left = first;
        let right = last;
        let leftValue = func.derivative(left);
        let rightValue = func.derivative(right);
        if (leftValue === null || rightValue === null || leftValue * rightValue > 0) return null;
        for (let iteration = 0; iteration < FunctionRootsSolver.MAXIMUM_ITERATIONS; iteration += 1) {
            const middle = (left + right) * 0.5;
            const middleValue = func.derivative(middle);
            if (middleValue === null) return null;
            if (Math.abs(right - left) <= tolerance || middleValue === 0) return middle;
            if (leftValue * middleValue <= 0) {
                right = middle;
                rightValue = middleValue;
            } else {
                left = middle;
                leftValue = middleValue;
            }
        }
        return null;
    }

    /** 使用 Puppy CAD 内核 黄金比例搜索正极小或负极大。 */
    private solveGoldenExtremum(
        func: ScalarFunction,
        targetValue: number,
        first: number,
        last: number,
        sign: 1 | -1,
        normalizedTolerance: number,
    ): number | null {
        let left = first;
        let right = last;
        let firstInterior = right - FunctionRootsSolver.GOLDEN_RATIO * (right - left);
        let secondInterior = left + FunctionRootsSolver.GOLDEN_RATIO * (right - left);
        const evaluate = (parameter: number): number => sign * ((func.value(parameter) ?? Number.NaN) - targetValue);
        let firstValue = evaluate(firstInterior);
        let secondValue = evaluate(secondInterior);
        if (!Number.isFinite(firstValue) || !Number.isFinite(secondValue)) return null;
        const relativeTolerance = 10 * normalizedTolerance;
        const pointTolerance = 0.001 * normalizedTolerance;
        for (let iteration = 0; iteration < FunctionRootsSolver.MAXIMUM_ITERATIONS; iteration += 1) {
            if (
                Math.abs(right - left) <= relativeTolerance * (Math.abs(firstInterior) + Math.abs(secondInterior) + 1)
                || Math.abs(secondInterior - firstInterior) <= pointTolerance
            ) break;
            if (firstValue <= secondValue) {
                right = secondInterior;
                secondInterior = firstInterior;
                secondValue = firstValue;
                firstInterior = right - FunctionRootsSolver.GOLDEN_RATIO * (right - left);
                firstValue = evaluate(firstInterior);
            } else {
                left = firstInterior;
                firstInterior = secondInterior;
                firstValue = secondValue;
                secondInterior = left + FunctionRootsSolver.GOLDEN_RATIO * (right - left);
                secondValue = evaluate(secondInterior);
            }
        }
        return firstValue <= secondValue ? firstInterior : secondInterior;
    }

    /** 按 Puppy CAD 内核 `AppendRoot` 参数容差有序插入并去重。 */
    private appendRoot(roots: number[], root: number, tolerance: number): void {
        if (roots.some((candidate) => Math.abs(candidate - root) <= tolerance)) return;
        roots.push(root);
        roots.sort((left, right) => left - right);
    }
}
